// Archive sync — the deterministic conversation-archive step shared by
// `session-start`, `session-end`, and the explicit `vortex catch-up`:
//
//   1. catch-up ingest — fold finished transcripts into
//      `data/_session-archive/` (via the optional memory-extended add-on);
//   2. raw-retention sweep — delete LOCAL raw transcript copies older than
//      `autoRecord.archiveRawRetentionDays` (raw is a byte-for-byte duplicate
//      kept for debugging; recall/reindex/cross-machine sync use only the
//      normalized files, which are kept forever);
//   3. pathspec auto-commit (`autoRecord.archiveCommit`) — commit EXACTLY the
//      archive's `raw/` + `normalized/` subtrees and nothing else, so archived
//      conversations never linger as "N uncommitted change(s)" noise in the
//      next session-start report. Deliberately NOT the whole
//      `_session-archive/` dir: the derived `metadata.sqlite` (+ sidecars) and
//      the `.sync.lock` below live there too, and an instance without a
//      .gitignore must never get them committed;
//   4. opt-in push (`sync.autoPush`) — fast-forward pull, then push. The one
//      configured exception to "push stays explicit"; a push sends every
//      unpushed commit on the branch, which the config doc spells out.
//
// Concurrency: multiple sessions can start/end at once (and a SessionEnd can
// overlap the next SessionStart). A repo-level lock file — same atomic
// `open("wx")` + ownership-token pattern as the vectorize setup lock — makes
// overlapping runs skip instead of fighting over the sqlite store and the git
// index. Skipping is always safe: whatever a skipped run would have done, the
// next session-start does.
//
// Every step is best-effort: a failure (missing add-on, locked index, git
// error) degrades to "skipped", never throws out of the hook.

import {
  closeSync,
  existsSync,
  mkdirSync,
  openSync,
  readdirSync,
  readFileSync,
  renameSync,
  rmSync,
  statSync,
  writeSync,
} from "node:fs";
import { execFileSync } from "node:child_process";
import { isAbsolute, join, relative, sep } from "node:path";
import type { ModuleContext, VortexConfig } from "@vortex-os/core";
import { commitFrameworkPaths } from "./git-commit.js";
import type { CatchUpOptions, CatchUpResult } from "./catch-up.js";

/**
 * Run a git command, returning its stdout; git's own stderr is suppressed so a
 * plain non-git folder never leaks `fatal: not a git repository` into hook
 * output. Callers treat a non-zero exit (throw) as "no git" / "step skipped".
 *
 * `GIT_TERMINAL_PROMPT=0` because every caller is a hook or agent path with no
 * human attached: a git that decides to ASK (credentials, host key) would
 * otherwise hang the hook until the host kills it — fail fast instead and let
 * the skip-reason surface in the report.
 */
export function gitOut(cwd: string, gitArgs: readonly string[]): string {
  return execFileSync("git", [...gitArgs], {
    cwd,
    encoding: "utf8",
    env: { ...process.env, GIT_TERMINAL_PROMPT: "0" },
    stdio: ["ignore", "pipe", "ignore"],
  });
}

/**
 * Network-touching git (fetch / pull / push) gets a time bound on top: a
 * stalled remote inside a SessionEnd hook would otherwise eat the whole
 * exit-kill window (and a session-start hook's patience) with nothing to show.
 * On expiry execFileSync signals the direct git child and throws — callers
 * already treat a throw as "step skipped". (Best-effort, not a guaranteed
 * process-TREE kill: a credential helper or ssh child git spawned can outlive
 * the bound on Windows — but git itself stops driving the operation.)
 */
const GIT_NET_TIMEOUT_MS = 15_000;
export function gitNet(
  cwd: string,
  gitArgs: readonly string[],
  timeoutMs: number = GIT_NET_TIMEOUT_MS,
): string {
  return execFileSync("git", [...gitArgs], {
    cwd,
    encoding: "utf8",
    timeout: timeoutMs,
    env: { ...process.env, GIT_TERMINAL_PROMPT: "0" },
    stdio: ["ignore", "pipe", "ignore"],
  });
}

/**
 * Detect an interrupted git operation — a near-certain crashed-session signal:
 * a merge / rebase / cherry-pick / revert / bisect left mid-flight, or a stray
 * index lock. Uses `git rev-parse --git-path` so the marker resolves correctly
 * even when `.git` is a FILE (a linked worktree or submodule), not a directory.
 * Returns the FIRST matching marker name, or null when the tree is clean / not
 * a git repo. Exported for tests. (Tier-1 carryover; decision-log 2026-06-04.)
 */
export function detectInterruptedGitOp(repoRoot: string): string | null {
  const markers = [
    "MERGE_HEAD",
    "rebase-merge",
    "rebase-apply",
    "CHERRY_PICK_HEAD",
    "REVERT_HEAD",
    "BISECT_LOG",
    "index.lock",
  ];
  try {
    const args = ["rev-parse", ...markers.flatMap((m) => ["--git-path", m])];
    const resolved = gitOut(repoRoot, args).split(/\r?\n/).map((s) => s.trim());
    for (let i = 0; i < markers.length; i++) {
      const p = resolved[i];
      if (p && existsSync(isAbsolute(p) ? p : join(repoRoot, p))) return markers[i]!;
    }
  } catch {
    // not a git repo / git error — no signal
  }
  return null;
}

/**
 * A `.git/index.lock` older than this is treated as crashed-session debris and
 * reclaimed at session start. git holds index.lock only for the brief span of an
 * index write (commit / add / merge), so a lock this old cannot belong to a live
 * operation; sized to match the archive sync's own crash TTL.
 */
const GIT_INDEX_LOCK_TTL_MS = 10 * 60 * 1000;

/**
 * Reclaim a stale `.git/index.lock` left by a crashed or killed prior session —
 * the common aftermath of an UNATTENDED delegated run whose terminal closed
 * before git released the lock. Such a lock blocks BOTH the session-start
 * fast-forward pull AND the archive auto-commit, so every following session
 * surfaces a false "diverged / uncommitted changes" warning until someone
 * deletes it by hand (observed recurring, 2026-06-19).
 *
 * Safe because it removes the lock ONLY when it is provably stale: git writes no
 * PID into index.lock (so — unlike the archive `.sync.lock` — the dead-PID probe
 * is unavailable and only the age signal applies), but git holds the lock for at
 * most the brief duration of an index write, so a lock older than the TTL cannot
 * be a live operation. A FRESH lock is left untouched (a real concurrent op may
 * hold it). ONLY `index.lock` is reclaimed — a genuine interrupted merge / rebase
 * / cherry-pick (MERGE_HEAD, rebase-merge, …) is deliberately left for the user
 * to reconcile, and `pull --ff-only` refuses into such a state regardless.
 *
 * `holderProvablyDead` is the escape from the TTL wait: git stamps no PID into
 * index.lock, but a killed archive sync leaves its `.sync.lock` (which DOES carry
 * a PID) beside the git lock. The ONLY caller that sets this true is `runArchiveSync`
 * — and only after it has RECLAIMED that dead-holder `.sync.lock` and now HOLDS it
 * (`reclaimedDeadHolder`), so no concurrent archive sync can be writing the index.
 * With that ownership-backed proof a still-FRESH index.lock is the dead session's
 * debris and is reclaimed at once, ending the recurring "killed mid-commit → fresh
 * lock survives 10 min, blocking the archive commit" (observed 2026-06-22). A bare
 * unowned peek must NOT pass this true (it could delete a live concurrent op's
 * lock). The TOCTOU re-stat guard still applies. The bypass's safety rests on the
 * instance discipline that VortEX-managed git runs serialize through `.sync.lock`;
 * an EXTERNAL git that ignores it and holds a fresh index.lock concurrently is the
 * residual case — bounded and benign exactly like the TTL path below (git's atomic
 * lock→index rename makes that op abort cleanly; the real index is untouched).
 *
 * Resolves the path via `git rev-parse --git-path` so it works when `.git` is a
 * FILE (a linked worktree or submodule), not a directory. Returns the reclaimed
 * lock's age in ms (for the report), or null when nothing was reclaimed (no lock,
 * a still-fresh lock with no dead-holder proof, or not a git repo). Best-effort:
 * never throws. Exported for tests.
 */
export function reclaimStaleGitIndexLock(
  repoRoot: string,
  nowMs: number = Date.now(),
  ttlMs: number = GIT_INDEX_LOCK_TTL_MS,
  holderProvablyDead: boolean = false,
): number | null {
  try {
    const rel = gitOut(repoRoot, ["rev-parse", "--git-path", "index.lock"]).trim();
    if (!rel) return null;
    const lock = isAbsolute(rel) ? rel : join(repoRoot, rel);
    let mtimeMs: number;
    try {
      mtimeMs = statSync(lock).mtimeMs;
    } catch {
      return null; // no lock present
    }
    const ageMs = nowMs - mtimeMs;
    // Fresh locks are normally left alone (a live op may hold one — git stamps no
    // PID, so age is the only signal). The exception: independent proof the holder
    // is dead (the adjacent archive `.sync.lock` named a dead PID) means a fresh
    // git lock is that dead session's debris → reclaim now, skip the TTL wait.
    if (!holderProvablyDead && ageMs < ttlMs) return null;
    // TOCTOU guard: re-stat immediately before unlinking and bail unless it is
    // still the SAME stale lock (mtime unchanged) — so a lock that was replaced
    // since the first stat is left alone. A microscopic residual window remains
    // (a fresh lock created AFTER this re-stat but BEFORE the unlink); it is
    // IRREDUCIBLE without cooperation from git (Node has no atomic
    // remove-if-mtime), but bounded and benign in practice:
    //   - The only realistic concurrent deleter of a >TTL-stale lock is ANOTHER
    //     reclaim (git never force-removes its own index.lock), and two reclaims
    //     target the same stale file → the loser's rmSync is a no-op (force
    //     ignores ENOENT). Reaching the dangerous case needs a real git index
    //     write to BEGIN inside that sub-millisecond gap on this same repo.
    //   - Even then it cannot corrupt the index: git writes index.lock then
    //     ATOMICALLY renames it onto index, so a vanished lock makes that rename
    //     fail and the op abort cleanly — at worst one best-effort automated git
    //     op errors out and self-heals next session; the real index is untouched.
    // This is strictly safer than the standard manual remedy (`rm
    // .git/index.lock`), which has no guard at all.
    try {
      if (statSync(lock).mtimeMs !== mtimeMs) return null; // replaced — leave it
    } catch {
      return null; // already gone — nothing to reclaim
    }
    rmSync(lock, { force: true });
    return ageMs;
  } catch {
    return null; // not a git repo / git error — best-effort
  }
}

/** Why the auto-commit step did not commit (when `committed` is false). */
export type ArchiveCommitSkip =
  | "off" // autoRecord.archiveCommit disabled
  | "git-busy" // interrupted op in progress (merge/rebase/lock) — never commit into that
  | "detached-head" // a commit here would be orphaned; leave it to the user
  | "nothing-to-commit"
  | "not-a-git-repo"
  | "git-error";

/** Why the opt-in push did not push (when `pushed` is false). */
export type ArchivePushSkip =
  | "off" // sync.autoPush not enabled (the default)
  | "no-commit" // nothing was committed this run and nothing was ahead
  | "no-remote"
  | "no-upstream"
  | "diverged" // behind upstream and not fast-forwardable — user must reconcile
  | "non-archive-commits-pending" // tick only: a human commit sits unpushed — the tick yields
  | "git-error";

export interface ArchiveSyncResult {
  /** False when the run was skipped wholesale (lock held by a concurrent run). */
  readonly ran: boolean;
  readonly lockSkipped: boolean;
  /** Ingest outcome; null when the add-on is absent or the ingest failed. */
  readonly catchUp: CatchUpResult | null;
  /** Raw transcript copies deleted by the retention sweep this run. */
  readonly rawPruned: number;
  /** The retention window the sweep used (0 = sweep disabled). */
  readonly retentionDays: number;
  readonly committed: boolean;
  readonly commitSkipped?: ArchiveCommitSkip;
  readonly pushed: boolean;
  readonly pushSkipped?: ArchivePushSkip;
  /**
   * Age (ms) of a stale `.git/index.lock` this run reclaimed while HOLDING the
   * `.sync.lock` (ownership-backed — either a dead-holder bypass or the age TTL).
   * Absent when nothing was reclaimed. Lets session start show the calm 🔓 line
   * and drop the pre-sync ⚠️ this reclaim resolved.
   */
  readonly gitLockReclaimedMs?: number;
}

const NOTHING: ArchiveSyncResult = {
  ran: false,
  lockSkipped: false,
  catchUp: null,
  rawPruned: 0,
  retentionDays: 0,
  committed: false,
  pushed: false,
};

/**
 * A sync whose lock is older than this is treated as crashed and reclaimed.
 * Long enough for a worst-case backlog ingest; short enough that a killed hook
 * (Claude Code's 60s hook timeout) never blocks archiving for a whole day.
 *
 * The TTL is the FALLBACK. The host is documented to kill SessionEnd hooks at
 * exit without waiting (claude-code #41577), and "exit, then immediately open
 * a new session" is a normal move — a TTL-only reclaim would make that new
 * session's start hook skip archiving for up to 10 minutes. So a lock whose
 * recorded PID is provably dead is reclaimed at once (`lockHolderDead`); the
 * TTL covers locks with no readable PID and same-machine PID reuse.
 */
const ARCHIVE_SYNC_LOCK_TTL_MS = 10 * 60 * 1000;

/**
 * How long the END phase (only) waits for a held lock before giving up.
 * Everywhere else the lock is skip-don't-wait — the next sync repeats the
 * work. At SessionEnd that net is weakest (the final tail would wait until
 * the next session), and with mid-session ticks the likeliest holder is an
 * async tick of THIS dying session: killed with the host moments later, its
 * lock is then dead-PID-reclaimed by one of the retry attempts. Bounded so
 * the exit grace is spent committing, not queueing.
 */
const END_LOCK_WAIT_MS = 1_500;

/**
 * True only when the lock file names a PID that provably no longer runs. The
 * token is `<pid>-<hrtime>` written by the holder right after acquiring; an
 * empty/foreign/unreadable lock yields false (fall back to the TTL). `kill(0)`
 * probes existence without signaling; EPERM means "exists, not ours" → alive.
 * The lock lives in the (uncommitted) local archive dir, so the PID is always
 * a this-machine PID — never one synced from another machine.
 */
function lockHolderDead(lock: string): boolean {
  let pid = 0;
  try {
    const m = /^(\d+)-/.exec(readFileSync(lock, "utf8").trim());
    if (!m) return false;
    pid = Number(m[1]);
  } catch {
    return false;
  }
  if (!Number.isSafeInteger(pid) || pid <= 0) return false;
  try {
    process.kill(pid, 0);
    return false; // signal delivered — still running
  } catch (e) {
    return (e as NodeJS.ErrnoException).code === "ESRCH";
  }
}

function lockPath(dataDir: string): string {
  return join(dataDir, "_session-archive", ".sync.lock");
}

/** Repo-relative POSIX path of `data/_session-archive` (for pathspecs / report). */
export function archiveRelPath(ctx: ModuleContext): string {
  return relative(ctx.repoRoot, join(ctx.dataDir, "_session-archive")).split(sep).join("/");
}

/**
 * Delete raw transcript copies older than `retentionDays` from
 * `<dataDir>/_session-archive/raw/`. Files only — directories stay, and the
 * normalized tree is never touched. Returns the number of files removed.
 * Exported for tests.
 */
export function sweepRawArchive(
  dataDir: string,
  retentionDays: number,
  nowMs: number = Date.now(),
): number {
  if (!(retentionDays > 0)) return 0;
  const rawDir = join(dataDir, "_session-archive", "raw");
  const cutoff = nowMs - retentionDays * 86_400_000;
  let pruned = 0;
  const walk = (dir: string): void => {
    let entries;
    try {
      entries = readdirSync(dir, { withFileTypes: true });
    } catch {
      return; // raw/ absent or unreadable — nothing to sweep
    }
    for (const e of entries) {
      const p = join(dir, e.name);
      if (e.isDirectory()) {
        walk(p);
      } else if (e.isFile()) {
        try {
          if (statSync(p).mtimeMs < cutoff) {
            rmSync(p, { force: true });
            pruned++;
          }
        } catch {
          // stat/rm race — skip this file
        }
      }
    }
  };
  walk(rawDir);
  return pruned;
}

/**
 * Clean up atomic-write temp files (`*.tmp-<pid>`). Two distinct sweeps:
 *
 * - **Committed subtrees (`raw/`, `normalized/`) — delete on sight, no age
 *   gate.** The archive store stages its temps in `.tmp/` (outside these
 *   subtrees) precisely so that NOTHING legitimate ever writes a temp here; a
 *   match can only be a stray, and removing it before `git add` guarantees a
 *   temp can never be committed under any crash timing.
 * - **The `.tmp/` staging dir — age-gated housekeeping.** A crash between
 *   write and rename strands the temp here, which is harmless (never
 *   committed); anything older than the lock TTL is clearly abandoned and is
 *   removed to keep the dir from accumulating.
 *
 * Exported for tests.
 */
export function sweepOrphanTempFiles(dataDir: string, nowMs: number = Date.now()): number {
  let removed = 0;
  const sweep = (dir: string, recurse: boolean, minAgeMs: number): void => {
    let entries;
    try {
      entries = readdirSync(dir, { withFileTypes: true });
    } catch {
      return;
    }
    for (const e of entries) {
      const p = join(dir, e.name);
      if (e.isDirectory()) {
        if (recurse) sweep(p, recurse, minAgeMs);
      } else if (e.isFile() && /\.tmp-\d+$/.test(e.name)) {
        try {
          if (minAgeMs === 0 || nowMs - statSync(p).mtimeMs >= minAgeMs) {
            rmSync(p, { force: true });
            removed++;
          }
        } catch {
          // stat/rm race — skip
        }
      }
    }
  };
  sweep(join(dataDir, "_session-archive", "raw"), true, 0);
  sweep(join(dataDir, "_session-archive", "normalized"), true, 0);
  sweep(join(dataDir, "_session-archive", ".tmp"), false, ARCHIVE_SYNC_LOCK_TTL_MS);
  return removed;
}

/** True when HEAD is detached (committing would orphan the commit). */
function isDetachedHead(repoRoot: string): boolean {
  try {
    gitOut(repoRoot, ["symbolic-ref", "-q", "HEAD"]);
    return false;
  } catch {
    return true;
  }
}

/**
 * The opt-in push step — push FIRST, reconcile only on rejection.
 *
 * The 0.14.2 shape (consult ahead/behind, ff-pull when behind, then push) was
 * disproved in the field 2026-06-11: ahead/behind against `@{upstream}` reads
 * the LAST-FETCHED remote ref, so after the other machine pushed, this machine
 * saw `behind=0` (stale), pushed blind, and the rejection surfaced as an
 * opaque "git-error". Only the push itself (or a fetch) talks to the remote —
 * so let the push decide, and spend a fetch only when it is rejected:
 *
 *   1. `ahead === 0` → nothing to push; but when the (possibly stale) ref
 *      already KNOWS we are behind, fast-forward first so non-end syncs keep
 *      realigning the local branch exactly as 0.14.2 did.
 *   2. `git push` → done in one network op in the common case.
 *   3. Rejected → `git fetch` once, recount against the now-true remote:
 *      - behind>0, ahead===0 — remote absorbed our commits some other way;
 *        ff-pull to realign, nothing left to push.
 *      - behind>0, ahead>0  — true divergence (both machines committed). NEVER
 *        auto-rebase here: a hook killed mid-rebase leaves an interrupted-op
 *        state that blocks every future auto-commit (detectInterruptedGitOp).
 *        Report "diverged"; the session-start report tells the user.
 *      - behind===0, ahead>0 — remote unchanged, so the failure was transient
 *        (network blip / lock on the server): retry the push ONCE.
 *   Never force-pushes, never retries more than once.
 *
 * Never throws; returns why it did not push.
 */
function pushCurrentBranch(repoRoot: string): { pushed: boolean; skip?: ArchivePushSkip } {
  try {
    if (!gitOut(repoRoot, ["remote"]).trim()) return { pushed: false, skip: "no-remote" };
  } catch {
    return { pushed: false, skip: "git-error" };
  }
  try {
    gitOut(repoRoot, ["rev-parse", "--abbrev-ref", "--symbolic-full-name", "@{upstream}"]);
  } catch {
    return { pushed: false, skip: "no-upstream" };
  }
  const counts = (): { ahead: number; behind: number } => {
    const [a, b] = gitOut(repoRoot, ["rev-list", "--left-right", "--count", "HEAD...@{upstream}"])
      .trim()
      .split(/\s+/);
    return { ahead: Number(a ?? 0) || 0, behind: Number(b ?? 0) || 0 };
  };
  try {
    // `ahead` is reliable even against a stale ref (local commits only ever
    // raise it); `behind` is NOT trusted to say "not behind" — the push itself
    // decides that. A stale ref that already says behind>0 is still actionable:
    // with nothing to push, realign by fast-forward (the 0.14.2 behavior).
    const before = counts();
    if (before.ahead === 0) {
      if (before.behind > 0) {
        try {
          gitNet(repoRoot, ["pull", "--ff-only"]);
        } catch {
          return { pushed: false, skip: "git-error" };
        }
      }
      return { pushed: false, skip: "no-commit" };
    }
    try {
      gitNet(repoRoot, ["push"]);
      return { pushed: true };
    } catch {
      gitNet(repoRoot, ["fetch"]);
      const after = counts();
      if (after.behind > 0 && after.ahead > 0) return { pushed: false, skip: "diverged" };
      if (after.behind > 0) {
        gitNet(repoRoot, ["pull", "--ff-only"]);
        return { pushed: false, skip: "no-commit" }; // realigned; nothing left to push
      }
      if (after.ahead > 0) {
        gitNet(repoRoot, ["push"]); // transient failure — one retry
        return { pushed: true };
      }
      return { pushed: false, skip: "git-error" };
    }
  } catch {
    return { pushed: false, skip: "git-error" };
  }
}

/**
 * Classify one commit: does it touch ONLY the archive subtrees
 * (`<archiveRel>/raw/`, `<archiveRel>/normalized/`)? This is the ground-truth
 * separator between the framework's own auto-sync commits and human-intended
 * work — judged by PATHS, never by commit-message convention (messages are
 * spoofable and drift). Conservative on anything unusual: a merge commit
 * (multiple parents) or an empty commit classifies as NOT archive-only.
 * Exported for tests and for git-reading features (e.g. worklog-gap
 * detection) that must not mistake auto-sync commits for human activity.
 */
export function isArchiveOnlyCommit(repoRoot: string, sha: string, archiveRel: string): boolean {
  try {
    const parents = gitOut(repoRoot, ["rev-list", "--parents", "-n", "1", sha])
      .trim()
      .split(/\s+/);
    if (parents.length !== 2) return false; // root commit (1) or merge (3+) — conservative
    const paths = gitOut(repoRoot, ["diff-tree", "--no-commit-id", "--name-only", "-r", sha])
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (paths.length === 0) return false; // empty commit — conservative
    const rawPrefix = `${archiveRel}/raw/`;
    const normPrefix = `${archiveRel}/normalized/`;
    return paths.every((p) => p.startsWith(rawPrefix) || p.startsWith(normPrefix));
  } catch {
    return false; // unreadable commit — conservative
  }
}

/**
 * The TICK's push step — the strict, autonomous variant of the boundary push.
 * A mid-session tick is the most autonomous actor in the system, so it gets
 * the narrowest mandate (decision-log 2026-06-12, hard constraints):
 *
 *   - it pushes ONLY when every unpushed commit is archive-only (per-commit
 *     path classification; one human commit in the range → hold and report
 *     `non-archive-commits-pending` — human work travels on a human act or
 *     the session-boundary autoPush contract, never on a background timer);
 *   - it pushes the CHECKED SHA explicitly (`<sha>:refs/heads/<branch>`), so
 *     a human commit landing between the check and the push can never ride
 *     along (the boundary push's bare `git push` would take HEAD);
 *   - it NEVER touches the working tree: no pull of any kind mid-session (a
 *     background ff-pull could move files under the user's feet). On
 *     rejection it fetches once, retries only if the new remote tip is an
 *     ancestor of the checked SHA, else reports `diverged` and yields to the
 *     next session boundary.
 */
function pushArchiveOnly(
  repoRoot: string,
  archiveRel: string,
): { pushed: boolean; skip?: ArchivePushSkip } {
  try {
    if (!gitOut(repoRoot, ["remote"]).trim()) return { pushed: false, skip: "no-remote" };
  } catch {
    return { pushed: false, skip: "git-error" };
  }
  let upstream: string;
  try {
    upstream = gitOut(repoRoot, ["rev-parse", "--abbrev-ref", "--symbolic-full-name", "@{upstream}"]).trim();
  } catch {
    return { pushed: false, skip: "no-upstream" };
  }
  const slash = upstream.indexOf("/");
  if (slash <= 0) return { pushed: false, skip: "git-error" };
  const remote = upstream.slice(0, slash);
  const branch = upstream.slice(slash + 1);
  try {
    const checkedHead = gitOut(repoRoot, ["rev-parse", "HEAD"]).trim();
    const unpushed = gitOut(repoRoot, ["rev-list", `${upstream}..${checkedHead}`])
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (unpushed.length === 0) return { pushed: false, skip: "no-commit" };
    for (const sha of unpushed) {
      if (!isArchiveOnlyCommit(repoRoot, sha, archiveRel)) {
        return { pushed: false, skip: "non-archive-commits-pending" };
      }
    }
    const refspec = `${checkedHead}:refs/heads/${branch}`;
    try {
      gitNet(repoRoot, ["push", remote, refspec]);
      return { pushed: true };
    } catch {
      gitNet(repoRoot, ["fetch", remote]);
      try {
        // Retry only when the moved remote tip is contained in what we
        // checked — a pure fast-forward for the remote, nothing unclassified.
        gitOut(repoRoot, ["merge-base", "--is-ancestor", upstream, checkedHead]);
      } catch {
        return { pushed: false, skip: "diverged" };
      }
      gitNet(repoRoot, ["push", remote, refspec]);
      return { pushed: true };
    }
  } catch {
    return { pushed: false, skip: "git-error" };
  }
}

/**
 * Run the archive sync (ingest → sweep → commit → optional push) under the
 * repo-level lock. `phase` picks the config gates the CALLER applies
 * (start/end honor `autoRecord.archive` / `archiveAtEnd`; "manual" = the
 * explicit `vortex catch-up`, which always ingests) — and at `"end"` it also
 * slims the run itself: the host kills SessionEnd hooks after a short,
 * undocumented grace at exit (claude-code #41577; field-confirmed 2026-06-11
 * on two machines, where the hook wrote the archive files but the commit/push
 * tail never ran). Everything deferrable is therefore deferred to the next
 * session start, so the time that remains goes to ingest → commit → push, in
 * that order — the commit is the durability step; a killed push leaves a
 * local commit the next sync pushes.
 */
export async function runArchiveSync(
  ctx: ModuleContext,
  config: VortexConfig,
  phase: "start" | "end" | "manual" | "tick",
  opts?: {
    /** Catch-up overrides — tests inject a sandbox home/fake adapters here. */
    readonly catchUp?: CatchUpOptions;
  },
): Promise<ArchiveSyncResult> {
  // ---- lock (skip-don't-wait; a skipped run is repeated at the next start;
  // the END phase alone retries briefly — see END_LOCK_WAIT_MS) ----
  const lock = lockPath(ctx.dataDir);
  const token = `${process.pid}-${process.hrtime.bigint()}`;
  // Set when this run reclaims a `.sync.lock` whose holder was provably DEAD (a
  // killed prior sync) — proof that any `.git/index.lock` it left is debris too,
  // so the git-lock reclaim below can clear it regardless of age (see line ~721).
  let reclaimedDeadHolder = false;
  const tryAcquire = (): number | "held" | "error" => {
    try {
      mkdirSync(join(ctx.dataDir, "_session-archive"), { recursive: true });
      return openSync(lock, "wx");
    } catch (e) {
      if ((e as NodeJS.ErrnoException).code !== "EEXIST") return "error";
    }
    let stale = true;
    let holderDead = false;
    try {
      holderDead = lockHolderDead(lock);
      stale = holderDead || Date.now() - statSync(lock).mtimeMs >= ARCHIVE_SYNC_LOCK_TTL_MS;
    } catch {
      stale = true; // unreadable lock → treat as stale
    }
    if (!stale) return "held";
    // Reclaim the stale lock via RENAME, not delete: between a stat and an
    // unlink another process could have reclaimed and re-created the lock, and
    // a blind delete would destroy that ACTIVE lock (two syncs would then run
    // at once — the race this lock exists to prevent). Rename is atomic and
    // exactly one reclaimer wins; the losers see ENOENT and treat the lock as
    // held by the winner. The winner still acquires through `open("wx")`, so
    // even a third process that slips in with a fresh lock first simply wins
    // instead of us.
    const claimed = `${lock}.stale-${process.pid}`;
    try {
      renameSync(lock, claimed);
      rmSync(claimed, { force: true });
    } catch {
      return "held"; // lost the reclaim race — another acquirer is active
    }
    try {
      const fd = openSync(lock, "wx");
      // Acquired AFTER reclaiming a dead-holder lock → tie the git-lock reclaim
      // to that same dead session (only on a successful acquire, so a lost
      // re-acquire race never sets the flag for a lock we do not hold).
      if (holderDead) reclaimedDeadHolder = true;
      return fd;
    } catch {
      return "held"; // lost the re-acquire race
    }
  };
  let acquired = tryAcquire();
  if (acquired === "held" && phase === "end") {
    const deadline = Date.now() + END_LOCK_WAIT_MS;
    while (acquired === "held" && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 100));
      acquired = tryAcquire();
    }
  }
  if (typeof acquired !== "number") return { ...NOTHING, lockSkipped: true };
  const lockFd: number = acquired;
  let tokenWritten = false;
  const releaseLock = (): void => {
    try {
      const cur = existsSync(lock) ? readFileSync(lock, "utf8").trim() : "";
      if (cur === token || (cur === "" && !tokenWritten)) rmSync(lock, { force: true });
    } catch {
      /* best effort — a stuck lock is reclaimed after the TTL */
    }
  };

  try {
    try {
      writeSync(lockFd, token + "\n");
      tokenWritten = true;
    } finally {
      closeSync(lockFd); // close so the lock file can be deleted later (Windows)
    }

    // ---- 1. ingest (best-effort; lean install without the add-on → null) ----
    // At "end" and "tick", pulled-file indexing is deferred (skipPulledIndex)
    // — see CatchUpOptions; the next start runs it unconditionally. The end
    // hook defers it for the kill-window budget; the tick defers it to keep
    // the per-turn background cost at "this session only".
    let catchUp: CatchUpResult | null = null;
    const slim = phase === "end" || phase === "tick";
    try {
      const { catchUpSessions } = await import("./catch-up.js");
      catchUp = await catchUpSessions(
        ctx,
        slim ? { ...opts?.catchUp, skipPulledIndex: true } : opts?.catchUp,
      );
    } catch {
      // archive backend unavailable (add-on absent or sqlite not built) — skip
    }

    // ---- 2. raw retention sweep (local-only duplicates) ----
    // Housekeeping, not correctness — skipped at "end"/"tick"; the next start
    // sweeps the same files.
    const retentionDays = config.autoRecord.archiveRawRetentionDays;
    let rawPruned = 0;
    if (!slim) {
      try {
        rawPruned = sweepRawArchive(ctx.dataDir, retentionDays);
      } catch {
        // best-effort tidy — ignore
      }
    }

    // ---- 3. pathspec auto-commit (raw/ + normalized/ ONLY — see header) ----
    // First clear crash orphans (`*.tmp-<pid>`) out of the committed subtrees
    // so `git add` can never stage one. See sweepOrphanTempFiles for why the
    // TTL age gate makes this safe against any legitimate in-flight write.
    try {
      sweepOrphanTempFiles(ctx.dataDir);
    } catch {
      // best-effort tidy — ignore
    }
    let committed = false;
    let commitSkipped: ArchiveCommitSkip | undefined;
    // Reclaim a stale `.git/index.lock` (crashed/killed prior session) before
    // the commit gate, so it does not falsely register as "git-busy" and skip
    // the archive commit. This covers EVERY archive-sync caller (session
    // start/end, mid-session tick, manual catch-up) — not just the session-start
    // pull, which is reclaimed separately upstream. Only a provably-stale lock is
    // removed; a genuine interrupted merge/rebase still yields "git-busy" below.
    // When this run reclaimed a DEAD-holder `.sync.lock`, the git lock it left is
    // debris too → reclaim it regardless of age (we hold the sync lock now, so no
    // concurrent archive sync can be writing the index), unblocking the commit.
    // Capture the age so session start can surface the calm 🔓 line and drop the
    // pre-sync ⚠️ this ownership-backed reclaim just resolved.
    const gitLockReclaimedMs = reclaimStaleGitIndexLock(
      ctx.repoRoot,
      Date.now(),
      GIT_INDEX_LOCK_TTL_MS,
      reclaimedDeadHolder,
    );
    if (!config.autoRecord.archiveCommit) {
      commitSkipped = "off";
    } else if (detectInterruptedGitOp(ctx.repoRoot)) {
      commitSkipped = "git-busy";
    } else if (isDetachedHead(ctx.repoRoot)) {
      commitSkipped = "detached-head";
    } else {
      const rel = archiveRelPath(ctx);
      // Only pass subtrees that actually have a visible change. Two `git add`
      // failure modes hide here: a pathspec matching NOTHING (fresh instance,
      // raw/ or normalized/ not created yet) is a fatal error, and a pathspec
      // naming only IGNORED files errors too — a user who gitignores raw/
      // (local-only copies) must not break the commit of normalized/. An
      // ignored or unchanged subtree shows nothing in `git status --porcelain`,
      // so filtering on that covers both.
      const paths = [`${rel}/raw`, `${rel}/normalized`].filter((p) => {
        if (!existsSync(join(ctx.repoRoot, ...p.split("/")))) return false;
        try {
          return gitOut(ctx.repoRoot, ["status", "--porcelain", "--", p]).trim().length > 0;
        } catch {
          return false;
        }
      });
      const ingested = catchUp?.ingestedLocal ?? 0;
      const result = commitFrameworkPaths(
        ctx.repoRoot,
        paths,
        `chore(vortex): session archive sync (${ingested} ingested, ${rawPruned} raw pruned)`,
      );
      committed = result.committed;
      if (!result.committed) {
        commitSkipped =
          result.reason === "nothing-to-commit" || result.reason === "no-paths"
            ? "nothing-to-commit"
            : result.reason === "not-a-git-repo"
              ? "not-a-git-repo"
              : "git-error";
      }
    }

    // ---- 4. opt-in push ----
    // Boundary phases (start/end/manual) keep the documented autoPush
    // contract: push the branch, every unpushed commit included. The TICK —
    // the autonomous mid-session actor — uses the strict archive-only path
    // instead (see pushArchiveOnly).
    let pushed = false;
    let pushSkipped: ArchivePushSkip | undefined;
    if (!config.sync.autoPush) {
      pushSkipped = "off";
    } else if (phase === "tick") {
      ({ pushed, skip: pushSkipped } = pushArchiveOnly(ctx.repoRoot, archiveRelPath(ctx)));
    } else {
      ({ pushed, skip: pushSkipped } = pushCurrentBranch(ctx.repoRoot));
    }

    return {
      ran: true,
      lockSkipped: false,
      catchUp,
      rawPruned,
      retentionDays,
      committed,
      ...(commitSkipped !== undefined ? { commitSkipped } : {}),
      pushed,
      ...(pushSkipped !== undefined ? { pushSkipped } : {}),
      ...(gitLockReclaimedMs !== null ? { gitLockReclaimedMs } : {}),
    };
  } finally {
    releaseLock();
  }
}
